/**
 * InvolucroDoSino — extraído do `NotificationBell` na peça G do D3 (passo 19, 11/10/2026). Dono do estado aberto/fechado e do
 * formato: folha de baixo no celular (`compacto`), Popover no computador. O conteúdo (o `PainelDoSino`) vem por função, que
 * recebe o `aoFechar` e o `titulo` certo para cada formato (`SheetTitle` na folha, `<h2>` no Popover). O sino interno e o do
 * portal usam este mesmo invólucro: as 44 px do X da folha são conta feita uma vez só.
 */
import React, { useState, type ReactElement, type ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

/**
 * Classes da folha do celular. A geometria vem de somar o que está dentro dela:
 * o puxador ocupa 12 px (`mt-2` + `h-1`) e o cabeçalho do painel tem `h-12`, então a
 * linha do cabeçalho vai de 12 a 60 px e o centro dela fica em 36 px.
 *
 * - `gap-0`: o `sheetVariants` traz `gap-4`, que empurraria o painel 16 px para baixo.
 * - `pb-[env(safe-area-inset-bottom)]`: com `viewport-fit=cover` a folha encosta na borda
 *   de baixo do iPhone; sem isso o rodapé de 48 px ficaria sob a barra de início.
 * - O X é o botão embutido do Sheet (`absolute right-4 top-4`, ícone de 16 px, alvo
 *   minúsculo). Daqui ele vira um alvo de 44 px (spec §6.8) centrado na linha do cabeçalho:
 *   `top-3.5` (14 px) = 36 − 44/2; `right-1` (4 px) + `w-11` (44 px) = 48 px, que é o `pr-12`
 *   que o cabeçalho do painel reserva no celular. O `>button` pega só o filho direto da
 *   folha (o X); os botões do painel estão um nível abaixo.
 */
const CLASSES_DA_FOLHA = cn(
  "flex max-h-[85dvh] flex-col gap-0 rounded-t-2xl p-0 pb-[env(safe-area-inset-bottom)]",
  "[&>button]:right-1 [&>button]:top-3.5 [&>button]:flex [&>button]:h-11 [&>button]:w-11",
  "[&>button]:items-center [&>button]:justify-center"
);

interface Props {
  /** O botão do sino (vira o `Trigger` por `asChild`). */
  gatilho: ReactElement;
  /** Celular: folha de baixo. */
  compacto: boolean;
  /** `data-testid` do painel nos dois formatos (Popover e folha): o portal mantém o `portal-notification-panel`. */
  idDeTesteDoPainel?: string;
  children: (a: { aoFechar: () => void; titulo: ReactNode }) => ReactNode;
}

export function InvolucroDoSino({ gatilho, compacto, idDeTesteDoPainel, children }: Props) {
  const [aberto, setAberto] = useState(false);
  const aoFechar = () => setAberto(false);

  if (compacto) {
    return (
      <Sheet open={aberto} onOpenChange={setAberto}>
        <SheetTrigger asChild>{gatilho}</SheetTrigger>
        <SheetContent
          side="bottom"
          aria-describedby={undefined}
          className={CLASSES_DA_FOLHA}
          data-testid={idDeTesteDoPainel}
        >
          <div
            aria-hidden="true"
            className="mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-muted-foreground/30"
          />
          {children({
            aoFechar,
            titulo: <SheetTitle className="text-base font-semibold">Notificações</SheetTitle>,
          })}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>{gatilho}</PopoverTrigger>
      <PopoverContent
        align="end"
        aria-label="Notificações"
        className="flex w-96 flex-col p-0"
        data-testid={idDeTesteDoPainel}
      >
        {children({
          aoFechar,
          titulo: <h2 className="text-sm font-semibold">Notificações</h2>,
        })}
      </PopoverContent>
    </Popover>
  );
}
